/**
 * Premium "Partner Coming Soon" — Teaser für Endkunden.
 *
 * Story: Ein neuer Partner steht in einer bestimmten Branche kurz vor dem
 * Live-Gang auf Allyver. Wir spielen MIT dem Mysterium:
 *
 *  - Logo des Partners stark unscharf, mit eleganter Vignette → „verhüllt".
 *  - Direkt darüber ein dezenter Schloss-Hint („BALD ENTHÜLLT").
 *  - Partner-Name groß und ebenfalls weichgezeichnet — pure Silhouette.
 *  - Klar lesbar: COMING SOON-Stempel, „Wer wird der nächste …?", Branche-
 *    Pill, „BALD EXKLUSIV AUF ALLYVER"-Footer.
 *  - Ein einziger eleganter Glass-Card-Rahmen — KEIN extra Container mehr
 *    hinter dem Logo (das wirkte wie ein graues Bild-im-Bild und nahm dem
 *    Teaser die Eleganz). Logo schwebt jetzt frei auf dem Card-Glas.
 *  - Cinematic Backdrop, dezente Scanlines.
 */
import type { SceneBuilder } from "../../reel/scenes.js";
import { brandBackdrop } from "../../primitives/brandBackdrop.js";
import {
  accentFor, clipOutroFade, combineSceneFragments, escapeHtml, gsapSetLine, makeTl,
  pickMediaSrc, typeSize, weights, withAlpha, colors as themeColors,
} from "../../reel/sceneShared.js";

export const partnerComingSoon: SceneBuilder = ({
  ctx, clipPrefix, inputs, clipDurationSec, clipStartSec,
}) => {
  const accent = accentFor(inputs);
  const isVertical = ctx.format === "9:16";
  const name = (inputs.text ?? "PARTNER").trim();
  const teaser = (inputs.subtext ?? "Bald exklusiv auf Allyver").trim();
  const branche = String(inputs.number ?? inputs.extras?.category ?? "Dienstleister").trim();
  const logoUrl = pickMediaSrc(inputs.media, 0);

  // ---- Sizes ---------------------------------------------------------------
  // Mehr Platz, weil Service-Bullets entfallen → Headline & Logo gewinnen
  // an Wirkung. Wir vergrößern die Schrift bewusst (Marketing-Hierarchie:
  // Name dominiert, alles andere unterstützt nur).
  const cardW = isVertical ? 880 : 720;
  const logoArea = isVertical ? 360 : 300;
  const stampSize = typeSize("md", ctx.format) * 0.82;
  const nameSize = typeSize(isVertical ? "hero" : "mega", ctx.format) * 0.66;
  const teaserSize = typeSize("sm", ctx.format) * 0.85;
  const brancheSize = typeSize("sm", ctx.format) * 0.78;
  const hintSize = typeSize("sm", ctx.format) * 0.82;
  const lockHintSize = typeSize("sm", ctx.format) * 0.72;

  // ---- IDs -----------------------------------------------------------------
  const t = makeTl(clipStartSec);
  const cardId = `${clipPrefix}-card`;
  const cardGlowId = `${clipPrefix}-card-glow`;
  const stampWrapId = `${clipPrefix}-stamp-wrap`;
  const stampId = `${clipPrefix}-stamp`;
  const logoSlotId = `${clipPrefix}-logo-slot`;
  const logoImgId = `${clipPrefix}-logo-img`;
  const lockHintId = `${clipPrefix}-lock-hint`;
  const nameWrapId = `${clipPrefix}-namewrap`;
  const nameId = `${clipPrefix}-name`;
  const hintId = `${clipPrefix}-hint`;
  const brancheId = `${clipPrefix}-branche`;
  const footerId = `${clipPrefix}-foot`;
  const scanId = `${clipPrefix}-scan`;

  const backdrop = brandBackdrop(ctx, `${clipPrefix}-bg`, { variant: "cinematic", accent });

  // ---- Logo (verhüllt) -----------------------------------------------------
  // Quadratischer Slot mit abgerundeten Ecken — wie gewohnt. Bewusst
  // KEIN dunkler Background-Verlauf und KEINE Vignette mehr (das war der
  // graue Kasten, der vorher wie ein zweiter Rahmen hinterm Bild aussah).
  // Stattdessen nur eine sehr dezente Akzent-Aura, sodass der Slot Tiefe
  // bekommt, ohne sich als Rechteck abzuzeichnen.
  const logoBlurPx = isVertical ? 24 : 20;
  const logoInner = logoUrl
    ? `<img id="${logoImgId}" src="${escapeHtml(logoUrl)}" alt="partner-logo" style="max-width:90%;max-height:90%;width:auto;height:auto;object-fit:contain;filter:blur(${logoBlurPx}px) saturate(0.85);opacity:0.88;" />`
    : `<div id="${logoImgId}" style="font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${nameSize * 1.6}px;letter-spacing:-3px;color:${themeColors.fg};opacity:0.5;filter:blur(${logoBlurPx}px);">?</div>`;

  // Schloss + „BALD ENTHÜLLT" — als Overlay zentriert über dem blurry Logo.
  const lockHtml = `<div id="${lockHintId}" style="position:absolute;left:0;right:0;top:50%;transform:translateY(-50%);display:flex;flex-direction:column;align-items:center;gap:10px;color:${withAlpha(themeColors.fg, 0.78)};text-shadow:0 2px 14px rgba(0,0,0,0.65);pointer-events:none;">
    <svg width="${isVertical ? 60 : 52}" height="${isVertical ? 60 : 52}" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.1" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
    <div style="font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${lockHintSize}px;letter-spacing:7px;text-transform:uppercase;opacity:0.92;">Bald enthüllt</div>
  </div>`;

  // ---- Name (blurry) + Sub-Hint -------------------------------------------
  const nameHtml = `<div id="${nameWrapId}" style="position:relative;display:flex;flex-direction:column;align-items:center;justify-content:center;width:100%;gap:14px;">
    <div style="position:relative;display:flex;align-items:center;justify-content:center;width:100%;min-height:${nameSize * 1.05}px;">
      <div id="${nameId}" style="font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${nameSize}px;line-height:1;letter-spacing:-2.5px;color:${themeColors.fg};text-align:center;filter:blur(20px);opacity:0.92;user-select:none;white-space:nowrap;">${escapeHtml(name)}</div>
    </div>
    <div id="${hintId}" style="font-family:'Inter',sans-serif;font-weight:${weights.semibold};font-size:${hintSize}px;letter-spacing:1.6px;text-transform:uppercase;color:${withAlpha(themeColors.fg, 0.62)};text-align:center;">Wer wird der nächste Allyver-Partner?</div>
  </div>`;

  // ---- Kategorie-Pill (Teaser) --------------------------------------------
  const brancheHtml = `<div id="${brancheId}" style="display:inline-flex;align-items:center;gap:12px;padding:12px 24px;border-radius:9999px;background:${withAlpha(accent, 0.2)};border:1px solid ${withAlpha(accent, 0.55)};box-shadow:0 6px 22px ${withAlpha(accent, 0.28)};font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${brancheSize}px;letter-spacing:2.8px;text-transform:uppercase;color:${accent};">
    <span style="opacity:0.65;">Kategorie</span><span style="opacity:0.45;">·</span><span>${escapeHtml(branche)}</span>
  </div>`;

  // ---- COMING SOON Stempel (oben mittig) ----------------------------------
  const stampHtml = `<div id="${stampWrapId}" style="position:absolute;top:-22px;left:50%;z-index:5;display:flex;flex-direction:column;align-items:center;">
    <div id="${stampId}" style="position:relative;display:inline-flex;align-items:center;gap:12px;padding:14px 28px;border-radius:9999px;background:linear-gradient(180deg, ${accent} 0%, ${withAlpha(accent, 0.85)} 100%);box-shadow:0 14px 42px ${withAlpha(accent, 0.6)}, inset 0 1px 0 ${withAlpha(themeColors.fg, 0.45)};font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${stampSize}px;letter-spacing:4px;text-transform:uppercase;color:${themeColors.bg};">
      <span style="width:9px;height:9px;border-radius:9999px;background:${themeColors.bg};box-shadow:0 0 10px ${themeColors.bg};animation:hyper-blink 1.2s ease-in-out infinite;"></span>
      <span>COMING SOON</span>
    </div>
  </div>`;

  // ---- Footer (BALD EXKLUSIV AUF ALLYVER) ---------------------------------
  const footerHtml = `<div id="${footerId}" style="display:inline-flex;align-items:center;gap:14px;font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${teaserSize}px;letter-spacing:5px;text-transform:uppercase;color:${withAlpha(themeColors.fg, 0.85)};">
    <span style="width:28px;height:2px;background:${accent};"></span>
    <span>${escapeHtml(teaser)}</span>
    <span style="width:28px;height:2px;background:${accent};"></span>
  </div>`;

  const scanHtml = `<div id="${scanId}" style="position:absolute;inset:0;pointer-events:none;background-image:repeating-linear-gradient(0deg, transparent 0, transparent 4px, ${withAlpha(themeColors.fg, 0.025)} 4px, ${withAlpha(themeColors.fg, 0.025)} 5px);mix-blend-mode:overlay;opacity:0.5;"></div>`;

  const padTopBot = isVertical ? 80 : 56;
  const cardPadV = isVertical ? 64 : 48;
  const cardPadH = isVertical ? 52 : 40;

  const html = `<style>@keyframes hyper-blink{0%,100%{opacity:0.45;}50%{opacity:1;}}</style>
${scanHtml}
<div id="${clipPrefix}-pad" style="position:absolute;inset:0;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:28px;padding:${padTopBot}px 60px;">
  <div id="${cardId}" style="position:relative;width:${cardW}px;max-width:92%;border-radius:36px;background:linear-gradient(165deg, ${withAlpha(themeColors.fg, 0.06)} 0%, ${withAlpha(themeColors.fg, 0.015)} 100%);border:1px solid ${withAlpha(themeColors.fg, 0.14)};box-shadow:0 40px 100px rgba(0,0,0,0.55), inset 0 1px 0 ${withAlpha(themeColors.fg, 0.16)};backdrop-filter:blur(18px);-webkit-backdrop-filter:blur(18px);padding:${cardPadV}px ${cardPadH}px;display:flex;flex-direction:column;align-items:center;gap:30px;">
    <div id="${cardGlowId}" aria-hidden="true" style="position:absolute;inset:-2px;border-radius:38px;background:linear-gradient(135deg, ${withAlpha(accent, 0.55)} 0%, transparent 35%, transparent 65%, ${withAlpha(accent, 0.4)} 100%);opacity:0;pointer-events:none;mix-blend-mode:screen;filter:blur(2px);"></div>
    ${stampHtml}

    <div id="${logoSlotId}" style="position:relative;width:100%;height:${logoArea}px;border-radius:24px;display:flex;align-items:center;justify-content:center;padding:18px;overflow:hidden;">
      ${logoInner}
      ${lockHtml}
    </div>

    ${nameHtml}
    ${brancheHtml}
  </div>
  ${footerHtml}
</div>`;

  // ---- Animation -----------------------------------------------------------
  const init = [
    gsapSetLine(`#${cardId}`, { opacity: 0, y: 60, scale: 0.94 }),
    gsapSetLine(`#${cardGlowId}`, { opacity: 0 }),
    gsapSetLine(`#${stampWrapId}`, { opacity: 0, scale: 0.4, rotation: -10, xPercent: -50 }),
    gsapSetLine(`#${logoSlotId}`, { opacity: 0, scale: 0.94 }),
    gsapSetLine(`#${lockHintId}`, { opacity: 0, y: 6 }),
    gsapSetLine(`#${nameWrapId}`, { opacity: 0, y: 12 }),
    gsapSetLine(`#${hintId}`, { opacity: 0 }),
    gsapSetLine(`#${brancheId}`, { opacity: 0, y: 14, scale: 0.9 }),
    gsapSetLine(`#${footerId}`, { opacity: 0, y: 12 }),
  ].join("\n");

  const tweens: string[] = [
    t.to(`#${cardId}`, { opacity: 1, y: 0, scale: 1, duration: 0.95, ease: "raw:\"power3.out\"" }, 0.05),
    t.fromTo(`#${cardGlowId}`, { opacity: 0 }, { opacity: 0.7, duration: 0.5, ease: "raw:\"power2.out\"" }, 0.2),
    t.to(`#${cardGlowId}`, { opacity: 0.18, duration: 0.7, ease: "raw:\"power2.inOut\"" }, 0.85),
    t.to(`#${logoSlotId}`, { opacity: 1, scale: 1, duration: 0.7, ease: "raw:\"back.out(1.4)\"" }, 0.55),
    t.to(`#${lockHintId}`, { opacity: 1, y: 0, duration: 0.55, ease: "raw:\"power2.out\"" }, 0.95),
    t.to(`#${stampWrapId}`, { opacity: 1, scale: 1, rotation: -6, duration: 0.6, ease: "raw:\"back.out(2.2)\"" }, 0.7),
    t.to(`#${nameWrapId}`, { opacity: 1, y: 0, duration: 0.55, ease: "raw:\"power2.out\"" }, 1.1),
    t.to(`#${hintId}`, { opacity: 1, duration: 0.5, ease: "raw:\"power2.out\"" }, 1.35),
    // Mini-Glitch auf dem Namen — verstärkt das „nicht ganz klar" Gefühl.
    t.fromTo(`#${nameId}`, { x: -6 }, { x: 6, duration: 0.07, repeat: 4, yoyo: true, ease: "raw:\"none\"" }, 1.2),
    t.to(`#${brancheId}`, { opacity: 1, y: 0, scale: 1, duration: 0.5, ease: "raw:\"back.out(1.6)\"" }, 1.6),
    t.to(`#${footerId}`, { opacity: 1, y: 0, duration: 0.5, ease: "raw:\"power2.out\"" }, 1.85),
  ];

  // Sanftes Schweben der gesamten Card — gibt dem Teaser Atemfrequenz,
  // ohne abzulenken.
  const dwell = Math.max(0.8, clipDurationSec - 2.6);
  tweens.push(t.to(`#${cardId}`, { y: -8, duration: dwell, ease: "raw:\"sine.inOut\"" }, 2.1));

  tweens.push(clipOutroFade(clipPrefix, clipDurationSec, clipStartSec, 10));

  return combineSceneFragments([backdrop, { html, init, tweens }]);
};
